import { FC, useEffect, useRef } from 'react';
import { translate } from '../../i18n';
import { isDesktop } from '../../platform';
import { AddPaymentMethodCard } from '../AddPaymentMethodCard';
import { MercadoPagoCard } from '../MercadoPagoCard';
import { PaymentMethodCard } from '../PaymentMethodCard';
import { paymentMethodItemId, type PaymentMethodItem } from './types';
import { paymentMethodCarouselStyles } from './styles';
import arrowRight from '../../assets/icons/arrowRight.svg';

export interface PaymentMethodCarouselProps {
  items: PaymentMethodItem[];
  selectedId?: string;
  onSelect: (item: PaymentMethodItem) => void;
  onAddPress: () => void;
  /** Eliminar una tarjeta: sin este manejador no se muestra la papelera. */
  onRemove?: (item: PaymentMethodItem) => void;
  /** Tarjeta en una línea, con menos datos. */
  compact?: boolean;
  /** La primera tarjeta de un pago en 2 no muestra el estado seleccionado. */
  highlightSelection?: boolean;
  testID?: string;
  /** Sin margen negativo: el scroll queda dentro del ancho del padre. */
  contained?: boolean;
}

/**
 * PaymentMethodCarousel — carrusel horizontal con los métodos de pago
 *
 * Cambios:
 * ✅ Estilos centralizados en styles.ts
 * ✅ i18n para hint text
 * ✅ Cero Tailwind, cero hardcodes
 */
export const PaymentMethodCarousel: FC<PaymentMethodCarouselProps> = ({
  items,
  selectedId,
  onSelect,
  onAddPress,
  onRemove,
  compact = false,
  highlightSelection = true,
  testID = 'payment-method-carousel',
  contained = false,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef({ active: false, x: 0, left: 0, moved: false });
  /** Última selección hecha por la propia persona (toque o scroll): no se vuelve a centrar sola. */
  const userSelectedIdRef = useRef<string | undefined>(undefined);
  /** Scroll iniciado por el efecto de centrado: no dispara selección ni analytics. */
  const programmaticScrollRef = useRef(false);
  const scrollEndTimerRef = useRef<number | null>(null);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;

  /**
   * Si la selección cambia desde afuera (p. ej. se agregó una tarjeta y queda seleccionada), el carrusel
   * se desplaza hasta ella. Sin esto, el centrado automático del scroll volvía a elegir la primera.
   */
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !selectedId || selectedId === userSelectedIdRef.current) {
      return;
    }
    const index = items.findIndex((item) => paymentMethodItemId(item) === selectedId);
    const element = index >= 0 ? (container.children[index] as HTMLElement | undefined) : undefined;
    if (!element) {
      return;
    }
    const target = element.offsetLeft + element.offsetWidth / 2 - container.clientWidth / 2;
    if (Math.abs(container.scrollLeft - target) >= 2) {
      programmaticScrollRef.current = true;
      container.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
      window.setTimeout(() => {
        programmaticScrollRef.current = false;
      }, 400);
    }
  }, [selectedId, items]);

  useEffect(
    () => () => {
      if (scrollEndTimerRef.current !== null) {
        window.clearTimeout(scrollEndTimerRef.current);
      }
    },
    [],
  );

  const selectCenteredItem = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const containerCenter = container.scrollLeft + container.clientWidth / 2;

    let closestItem: PaymentMethodItem | null = null;
    let closestDistance = Infinity;

    items.forEach((item, index) => {
      const itemElement = container.children[index] as HTMLElement;
      if (!itemElement) return;

      const itemCenter = itemElement.offsetLeft + itemElement.offsetWidth / 2;
      const distance = Math.abs(itemCenter - containerCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestItem = item;
      }
    });

    if (!closestItem) return;

    const closestId = paymentMethodItemId(closestItem);
    if (closestId === selectedIdRef.current) return;

    userSelectedIdRef.current = closestId;
    onSelectRef.current(closestItem);
  };

  /**
   * Al terminar el scroll (gesto del usuario), selecciona la tarjeta centrada una sola vez.
   */
  const handleScroll = () => {
    if (programmaticScrollRef.current) return;

    if (scrollEndTimerRef.current !== null) {
      window.clearTimeout(scrollEndTimerRef.current);
    }
    scrollEndTimerRef.current = window.setTimeout(() => {
      scrollEndTimerRef.current = null;
      selectCenteredItem();
    }, 120);
  };

  // Encontrar el item seleccionado
  const selectedItem = items.find((item) => selectedId === paymentMethodItemId(item));

  /**
   * Sin selección (p. ej. tras recargar) el primer item es el que está a la vista, así que se muestra
   * con el alto de una tarjeta activa en vez del colapsado.
   */
  const activeId = selectedItem ? selectedId : items[0] ? paymentMethodItemId(items[0]) : undefined;

  // Mostrar hint solo bajo Mercado Pago o AddPaymentMethodCard
  const showHint =
    items.length > 1 &&
    (isDesktop || selectedItem?.kind === 'mercadopago' || selectedItem?.kind === 'add');

  return (
    <div
      style={contained ? paymentMethodCarouselStyles.contained : paymentMethodCarouselStyles.container}
      data-testid={testID}
    >
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className={contained ? 'payment-carousel-contained' : undefined}
        style={
          contained
            ? paymentMethodCarouselStyles.containedScroll
            : paymentMethodCarouselStyles.scrollContainer
        }
        onPointerDown={
          contained
            ? (event) => {
                if (event.pointerType === 'touch') return;
                const el = event.currentTarget;
                dragRef.current = { active: true, x: event.clientX, left: el.scrollLeft, moved: false };
                el.setPointerCapture(event.pointerId);
              }
            : undefined
        }
        onPointerMove={
          contained
            ? (event) => {
                const drag = dragRef.current;
                if (!drag.active) return;
                const delta = event.clientX - drag.x;
                if (Math.abs(delta) > 4) drag.moved = true;
                event.currentTarget.scrollLeft = drag.left - delta;
              }
            : undefined
        }
        onPointerUp={
          contained
            ? () => {
                dragRef.current.active = false;
              }
            : undefined
        }
        onClickCapture={
          contained
            ? (event) => {
                if (!dragRef.current.moved) return;
                event.preventDefault();
                event.stopPropagation();
                dragRef.current.moved = false;
              }
            : undefined
        }
      >
        {items.map((item) => {
          const id = paymentMethodItemId(item);
          const isSelected = activeId === id;

          return (
            <div
              key={id}
              style={
                contained
                  ? paymentMethodCarouselStyles.containedItem
                  : paymentMethodCarouselStyles.carouselItem
              }
              data-testid={`carousel-item-${id}`}
            >
              {item.kind === 'add' && (
                <AddPaymentMethodCard
                  isSelected={isSelected}
                  onPress={onAddPress}
                  testID={`${id}-add`}
                />
              )}

              {item.kind === 'card' && (
                <PaymentMethodCard
                  issuerName={item.issuerName}
                  last4={item.last4}
                  holderName={item.holderName}
                  expiry={item.expiry}
                  network={item.network}
                  networkVariant={item.networkVariant}
                  gradientColors={item.gradientColors}
                  isSelected={highlightSelection && isSelected}
                  isFocused={isSelected}
                  compact={compact}
                  onPress={() => {
                    userSelectedIdRef.current = id;
                    onSelect(item);
                  }}
                  onRemove={onRemove ? () => onRemove(item) : undefined}
                  testID={`${id}-card`}
                  accessibilityLabel={`${item.issuerName} tarjeta terminada en ${item.last4}`}
                />
              )}

              {item.kind === 'mercadopago' && (
                <MercadoPagoCard
                  isSelected={isSelected}
                  onPress={() => {
                    userSelectedIdRef.current = id;
                    onSelect(item);
                  }}
                  testID={`${id}-mercadopago`}
                />
              )}
            </div>
          );
        })}
      </div>

      {showHint && (
        <p style={paymentMethodCarouselStyles.hint} data-testid={`${testID}-hint`}>
          {translate(isDesktop ? 'paymentsUI.carousel.scrollHintWeb' : 'paymentsUI.carousel.scrollHint')}
          { isDesktop && <img src={arrowRight} alt="" style={paymentMethodCarouselStyles.arrow} /> }
        </p>
      )}
    </div>
  );
};

export type { PaymentMethodItem } from './types';
export { ADD_PAYMENT_METHOD_ID, paymentMethodItemId } from './types';
